/**
 * App-wide fix for "ResizeObserver loop completed with undelivered notifications"
 * (and the older "ResizeObserver loop limit exceeded").
 *
 * Every size-watcher in the app — MUI's auto-growing text boxes, the Word (docx) preview,
 * charts, our tables — is a ResizeObserver. When one of them changes a size inside its own
 * callback, the browser can't deliver every notice in the same frame and reports this
 * (harmless) notice; the development server turns it into a full-screen red error.
 *
 * Here every ResizeObserver callback is run in a 0ms timer instead (just after the browser's
 * notification step), so a size change made in a callback is picked up on the next frame — the
 * "loop" can no longer happen, whichever library created the observer. A timer (not
 * requestAnimationFrame) so it also runs when the page is not being drawn. Not visible.
 *
 * Imported first in src/index.jsx, before any component creates an observer.
 */
if (typeof window !== "undefined" && window.ResizeObserver && !window.ResizeObserver.__frameSafe) {
  const NativeResizeObserver = window.ResizeObserver;

  class FrameSafeResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      let frame = 0;
      let disconnected = false;
      // latest entry per observed element, so notices that arrive before the timer runs are
      // delivered together and none is lost
      const pending = new Map();
      super((entries, observer) => {
        entries.forEach((entry) => pending.set(entry.target, entry));
        if (frame) return;
        frame = setTimeout(() => {
          frame = 0;
          const batch = [...pending.values()];
          pending.clear();
          if (!disconnected && batch.length) callback(batch, observer);
        }, 0);
      });
      this.__stop = () => {
        disconnected = true;
        clearTimeout(frame);
        frame = 0;
        pending.clear();
      };
      this.__start = () => { disconnected = false; };
    }

    observe(...args) {
      this.__start();
      return super.observe(...args);
    }

    disconnect() {
      this.__stop();
      return super.disconnect();
    }
  }

  FrameSafeResizeObserver.__frameSafe = true;
  window.ResizeObserver = FrameSafeResizeObserver;
}
